'use client';

import { createRoute } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import { Button, Skeleton } from '@/components/ui';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/consulting',
  head: () => seoHead('/consulting'),
  component: Consulting,
});

const CALENDLY_URL = 'https://calendly.com/bryce-mindrealm/30min';

// The both-halves moat: the seasoned engineer who also lives inside AI tooling.
const proofPoints: string[] = [
  'Led, hired, and mentored engineering teams as a staff engineer, tech lead, and engineering manager.',
  'Found and reported real security vulnerabilities in Python and Perl, with public CVEs.',
  'Technical reviewer on ten-plus Manning software engineering books.',
  'Taught data science and web development in Python, Ruby, and TypeScript, as an instructor at The Iron Yard.',
  'This past year I built a production AI code reviewer, solo, in about six months, and catalogued more than 6,000 documented ways AI agents get code confidently wrong. It runs 75+ deterministic checks across Go, Python, TypeScript, and Rust, and returns the same findings every run.',
];

// Each mode also seeds Mindrealm seats. Accent alternates green/purple for rhythm.
const modes: {
  name: string;
  tagline: string;
  body: string;
  accent: 'primary' | 'secondary';
}[] = [
  {
    name: 'Assess',
    tagline: 'Find the risk.',
    accent: 'primary',
    body: 'I read your codebase by hand and with my own tooling, and I read your team the way a tech lead and engineering manager does. I sit with your engineers, watch how they actually work, and listen: how they use their tools, where their time goes, where they get stuck, and where morale, trust, or ownership is quietly costing you. You get a ranked findings report and a concrete roadmap to make AI-assisted work safer to ship. This is the fastest way to start.',
  },
  {
    name: 'Build',
    tagline: 'Build it right.',
    accent: 'secondary',
    body: 'I embed as a senior engineer and architect and build your guardrails with you: custom rules that encode your standards, and gates that stop bad code before it merges. Beyond that, I help your team build AI features into your product and set up their coding agents, hands on keyboard, pairing as we go so your engineers level up and keep the skills, habits, and automated processes after I leave. Project-based or a few days a week.',
  },
  {
    name: 'Coach',
    tagline: 'Level up the team.',
    accent: 'primary',
    body: "I level up your engineers on doing AI-assisted work well: hooks, skills, subagents, prompt technique, and the clean-code fundamentals the AI skips, SOLID, DRY, and putting correctness and security ahead of clever code. Plus the human review tools can't replace: access-control leaks, auth checks that pass when they should fail, and business logic that gives away refunds or admin rights. Done with your team, monthly or as a focused cohort.",
  },
  {
    name: 'Advise',
    tagline: 'Steer the organization.',
    accent: 'secondary',
    body: 'Fractional CTO and engineering-leadership help. I steer your organization through the AI transition without the quality cliff: how to structure teams, set the standards and metrics that keep the bar high, decide what AI tooling to build versus buy, and hire and level up engineers for a world where the AI writes the first draft. For founders and engineering leaders who want a seasoned operator in the room.',
  },
];

const auditDeliverables: string[] = [
  'A risk-ranked findings report on your real code. Security, correctness, concurrency, performance, and error handling, each with its exact location and severity.',
  "An architecture and code-quality read: where your design will and won't scale, where structure has rotted, and the refactors that pay down the most risk.",
  'A map of where your AI is confidently wrong, named against 6,000+ documented ways agents fail, such as tests that assert mocks instead of behavior, error handlers that swallow real bugs, and duplication dressed up as reuse.',
  'A teardown of your AI workflow. How your hooks, skills, subagents, and rules are wired, how your engineers prompt, where reviews bottleneck, which gates are missing, and where quality slips through.',
  'A read on how AI-assisted work actually flows through your team: where reviews bottleneck, where engineers lean too hard on the agent, and where process, ownership, or morale is quietly costing you throughput.',
  'A 30/60/90 roadmap. What to fix now, what to guard against, what to coach.',
  'A starter set of custom rules, hooks, skills, and subagents that encode your standards, plus a CI gate config. Yours to keep, paying off after I leave.',
  'A live readout with your engineering leads, walking the findings and the plan.',
];

function CtaButton({ label }: { label: string }) {
  return (
    <Button
      size="lg"
      asChild
      className="text-lg font-semibold text-white transition-all duration-200 hover:!text-white hover:!opacity-100 hover:brightness-110"
      style={{
        background: theme.buttons.gradients.primary.light,
        boxShadow: theme.shadows.button.primary.light.default,
        textShadow: theme.shadows.text.primary,
        padding: '1rem 2rem',
        height: 'auto',
      }}
    >
      <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    </Button>
  );
}

// Mirrors the real page layout (hero, problem card, why-me, four-mode grid,
// audit box) so the loading state doesn't pop into a much longer page.
function ConsultingSkeleton() {
  return (
    <MarketingLayout isLoading={true} isSignedIn={false}>
      {/* Hero */}
      <section className="px-6 pt-24 pb-12 md:px-16 md:pt-32">
        <div className="mx-auto flex max-w-[820px] flex-col items-center">
          <Skeleton className="mt-2 mb-3 h-10 w-[65%] md:h-12" />
          <Skeleton className="mb-6 h-10 w-[65%] md:h-12" />
          <Skeleton className="mb-8 h-7 w-[60%]" />
          <Skeleton className="h-12 w-44 rounded-lg" />
        </div>
      </section>
      {/* Problem card */}
      <section className="px-6 py-12 md:px-16 md:py-16">
        <Skeleton className="mx-auto h-40 max-w-[760px] rounded-2xl" />
      </section>
      {/* Why me */}
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[760px]">
          <Skeleton className="mb-6 h-9 w-56" />
          <Skeleton className="mb-8 h-6 w-[80%]" />
          <div className="space-y-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-[95%]" />
            <Skeleton className="h-5 w-[90%]" />
            <Skeleton className="h-5 w-[97%]" />
          </div>
        </div>
      </section>
      {/* Four modes grid */}
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1000px]">
          <Skeleton className="mx-auto mb-4 h-9 w-72" />
          <Skeleton className="mx-auto mb-2 h-5 w-[80%] max-w-[600px]" />
          <Skeleton className="mx-auto mb-10 h-5 w-[55%] max-w-[600px]" />
          <div className="grid gap-6 md:grid-cols-2">
            <Skeleton className="h-44 rounded-xl" />
            <Skeleton className="h-44 rounded-xl" />
            <Skeleton className="h-44 rounded-xl" />
            <Skeleton className="h-44 rounded-xl" />
          </div>
        </div>
      </section>
      {/* Audit box */}
      <section className="px-6 py-12 md:px-16 md:py-16">
        <Skeleton className="mx-auto h-96 max-w-[800px] rounded-2xl" />
      </section>
    </MarketingLayout>
  );
}

function ConsultingContent() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const showLoading = !mounted;
  const isDark = mounted && resolvedTheme === 'dark';

  if (showLoading) {
    return <ConsultingSkeleton />;
  }

  return (
    <MarketingLayout isLoading={false} isSignedIn={false}>
      {/* Hero */}
      <section className="px-6 pt-24 pb-12 md:px-16 md:pt-32">
        <div className="mx-auto max-w-[820px] text-center">
          <h1
            className="text-gradient-hero mb-5 text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: theme.fonts.display, lineHeight: 1.2 }}
          >
            AI made your team faster. <br />
            I'll help make them better.
          </h1>
          <p
            className="mb-8 text-2xl font-semibold md:text-3xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            Making AI-generated code safer to ship{' '}
            <br className="hidden md:inline" />
            by leveling up the team building it.
          </p>
          <CtaButton label="Book a call" />
        </div>
      </section>

      {/* Problem */}
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div
          className="mx-auto max-w-[760px] rounded-2xl p-8 text-center md:p-12"
          style={{
            background: theme.box.gradient.bg,
            border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
          }}
        >
          <p
            className="text-foreground/80 mb-6 text-lg md:text-xl"
            style={{ fontFamily: theme.fonts.body }}
          >
            Your agents write code fast. Nobody's sure it's right. Reviews pile
            up, subtle bugs slip through, and AI pull requests get a
            rubber-stamp 'LGTM' instead of a real look. Speed went up. But so
            did the tech debt.
          </p>
          <p
            className="text-2xl font-bold md:text-3xl"
            style={{
              fontFamily: theme.fonts.display,
              color: theme.colors.accent.primary[isDark ? 'dark' : 'light'],
            }}
          >
            I fix that.
          </p>
        </div>
      </section>

      {/* Proof */}
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[760px]">
          <h2
            className="text-gradient-hero mb-6 text-3xl font-bold tracking-tight md:text-4xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            Why work with me
          </h2>
          <p
            className="text-foreground/80 mb-8 text-lg"
            style={{ fontFamily: theme.fonts.body }}
          >
            I've spent 20 years holding the senior bar and the last year at the
            frontier of AI code quality.
          </p>
          <ul
            className="mb-8 space-y-4 text-base md:text-lg"
            style={{ fontFamily: theme.fonts.body }}
          >
            {proofPoints.map((point) => (
              <li key={point} className="flex items-start gap-3">
                <span
                  className="mt-1.5"
                  style={{
                    color:
                      theme.colors.accent.primary[isDark ? 'dark' : 'light'],
                  }}
                >
                  ✓
                </span>
                <span className="text-foreground/80">{point}</span>
              </li>
            ))}
          </ul>
          <p
            className="text-foreground/80 text-lg"
            style={{ fontFamily: theme.fonts.body }}
          >
            So I know exactly where AI breaks, and how to make it ship quality
            work you would put your name on. The seasoned engineer who hasn't
            lived inside AI tooling can't see where it breaks; the AI enthusiast
            often can't tell good from plausible. I've lived both.
          </p>
        </div>
      </section>

      {/* Four modes */}
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1000px]">
          <h2
            className="text-gradient-hero mb-4 text-center text-3xl font-bold tracking-tight md:text-4xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            Four ways to work with me
          </h2>
          <p
            className="text-foreground/70 mx-auto mb-10 max-w-[640px] text-center text-lg"
            style={{ fontFamily: theme.fonts.body }}
          >
            Four distinct engagements, from a one-time audit to ongoing work.
            Most start with Assess, the fixed-price audit below. Build, Coach,
            and Advise are longer-term and hands-on.
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            {modes.map((mode) => {
              const accent = theme.colors.accent[mode.accent];
              return (
                <div
                  key={mode.name}
                  className="rounded-xl p-6"
                  style={{
                    background: isDark
                      ? `${accent.dark}1a`
                      : `${accent.light}14`,
                    border: `1px solid ${isDark ? `${accent.dark}4d` : `${accent.light}33`}`,
                  }}
                >
                  <h3
                    className="mb-1 text-xl font-semibold md:text-2xl"
                    style={{
                      fontFamily: theme.fonts.display,
                      color: accent[isDark ? 'dark' : 'light'],
                    }}
                  >
                    {mode.name}
                  </h3>
                  <p
                    className="text-foreground/90 mb-3 text-base font-semibold"
                    style={{ fontFamily: theme.fonts.body }}
                  >
                    {mode.tagline}
                  </p>
                  <p
                    className="text-foreground/70 text-base"
                    style={{ fontFamily: theme.fonts.body }}
                  >
                    {mode.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lead offer: the audit */}
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div
          className="mx-auto max-w-[800px] rounded-2xl p-8 md:p-12"
          style={{
            background: theme.box.gradient.bg,
            border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
          }}
        >
          <h2
            className="text-gradient-hero mb-4 text-center text-3xl font-bold tracking-tight md:text-4xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            Start with an audit.
          </h2>
          <p
            className="text-foreground/80 mx-auto mb-8 max-w-[620px] text-center text-base md:text-lg"
            style={{ fontFamily: theme.fonts.body }}
          >
            One sprint, fixed price, starting at $5,000. My software runs a
            deterministic review across your whole codebase, and I bring the
            senior read on top. You get a clear, ranked picture of where you're
            weak and the fastest path to fix it, plus a working session with
            your team so you feel what going deeper together looks like.
          </p>
          <ul
            className="mx-auto mb-8 max-w-[640px] space-y-3 text-left text-base"
            style={{ fontFamily: theme.fonts.body }}
          >
            {auditDeliverables.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span
                  className="mt-1"
                  style={{
                    color:
                      theme.colors.accent.primary[isDark ? 'dark' : 'light'],
                  }}
                >
                  ✓
                </span>
                <span className="text-foreground/80">{item}</span>
              </li>
            ))}
          </ul>
          <p
            className="text-foreground/60 mx-auto max-w-[620px] text-center text-sm md:text-base"
            style={{ fontFamily: theme.fonts.body }}
          >
            You leave knowing what deserves attention now, what can wait, and
            where senior engineering time will pay off most.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-12 pb-20 md:px-16 md:py-20 md:pb-24">
        <div className="mx-auto max-w-[760px] text-center">
          <p
            className="text-foreground/70 mb-4 text-lg md:text-xl"
            style={{ fontFamily: theme.fonts.body }}
          >
            Tell me where you're stuck, in the code and on the team,{' '}
            <br className="hidden md:inline" />
            and I'll show you the fastest way to fix it.
          </p>
          <h2
            className="text-gradient-hero mb-8 text-3xl font-bold tracking-tight md:text-4xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            Book a free 30-minute intro call.
          </h2>
          <CtaButton label="Book a call" />
        </div>
      </section>
    </MarketingLayout>
  );
}

function Consulting() {
  return (
    <Suspense fallback={<ConsultingSkeleton />}>
      <ConsultingContent />
    </Suspense>
  );
}
